import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cmsApi } from '@/services/cmsApi';
import { trackEvent } from '@/lib/analytics';

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  product?: {
    title: string;
    price: number;
    image_url: string;
    slug: string;
    currency: string;
    brand_id?: string | null;
  };
}

interface CartContextType {
  cartId: string | null;
  items: CartItem[];
  itemCount: number;
  isLoading: boolean;
  isAdding: boolean;
  addToCart: (productId: string, showToast?: boolean, source?: string) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const SESSION_ID_KEY = 'user_session_id';

const getSessionId = (): string => {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartId, setCartId] = useState<string | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const { toast } = useToast();
  const initializingRef = useRef(false);
  const userIdRef = useRef<string | undefined>(undefined);

  // Track auth state for analytics
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      userIdRef.current = session?.user?.id;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      userIdRef.current = session?.user?.id;
    });

    return () => subscription.unsubscribe();
  }, []);

  // Initialize cart and listen for auth changes
  useEffect(() => {
    initializeCart();

    // Listen for auth state changes to handle login/logout
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        // Re-initialize cart when auth state changes
        initializeCart();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const initializeCart = async () => {
    // Prevent concurrent initialization
    if (initializingRef.current) return;
    initializingRef.current = true;

    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // AUTHENTICATED USER: Always use user-linked cart
        await initializeAuthenticatedCart(user.id);
      } else {
        // ANONYMOUS USER: Use localStorage cart
        await initializeAnonymousCart();
      }
    } catch (error) {
      console.error('Error initializing cart:', error);
    } finally {
      setIsLoading(false);
      initializingRef.current = false;
    }
  };

  const initializeAuthenticatedCart = async (userId: string) => {
    // Check if there's an anonymous cart to merge
    const storedCartId = localStorage.getItem('cartId');
    let anonymousCartItems: { product_id: string; quantity: number }[] = [];

    if (storedCartId) {
      // Fetch anonymous cart items before we switch carts
      const { data: anonCart } = await supabase
        .from('carts')
        .select('id, user_id')
        .eq('id', storedCartId)
        .maybeSingle();

      // Only merge if it's actually an anonymous cart (no user_id)
      if (anonCart && !anonCart.user_id) {
        const { data: anonItems } = await supabase
          .from('cart_items')
          .select('product_id, quantity')
          .eq('cart_id', storedCartId);
        
        anonymousCartItems = anonItems || [];
      }
    }

    // Find or create user's cart
    let { data: userCart } = await supabase
      .from('carts')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (!userCart) {
      // Create new cart for user
      const { data: newCart, error } = await supabase
        .from('carts')
        .insert({ user_id: userId, session_id: null })
        .select('id')
        .single();

      if (error) throw error;
      userCart = newCart;
    }

    // Merge anonymous cart items into user's cart
    if (anonymousCartItems.length > 0 && userCart) {
      await mergeCartItems(userCart.id, anonymousCartItems);
      
      // Delete the old anonymous cart
      if (storedCartId) {
        await supabase.from('cart_items').delete().eq('cart_id', storedCartId);
        await supabase.from('carts').delete().eq('id', storedCartId);
      }
    }

    // Update localStorage to point to user's cart
    localStorage.setItem('cartId', userCart.id);
    setCartId(userCart.id);
    await loadCartItems(userCart.id);
  };

  const initializeAnonymousCart = async () => {
    const storedCartId = localStorage.getItem('cartId');
    let existingCart = null;

    if (storedCartId) {
      // Try to load the stored anonymous cart
      const { data } = await supabase
        .from('carts')
        .select('id')
        .eq('id', storedCartId)
        .is('user_id', null) // Ensure it's an anonymous cart
        .maybeSingle();
      
      existingCart = data;
    }

    // Create new anonymous cart if none found
    if (!existingCart) {
      const sessionId = crypto.randomUUID();
      const { data: newCart, error } = await supabase
        .from('carts')
        .insert({ user_id: null, session_id: sessionId })
        .select('id')
        .single();

      if (error) throw error;
      existingCart = newCart;
    }

    localStorage.setItem('cartId', existingCart.id);
    setCartId(existingCart.id);
    await loadCartItems(existingCart.id);
  };

  const mergeCartItems = async (targetCartId: string, itemsToMerge: { product_id: string; quantity: number }[]) => {
    // Get existing items in target cart
    const { data: existingItems } = await supabase
      .from('cart_items')
      .select('product_id, quantity, id')
      .eq('cart_id', targetCartId);

    const existingMap = new Map(existingItems?.map(i => [i.product_id, i]) || []);

    for (const item of itemsToMerge) {
      const existing = existingMap.get(item.product_id);
      
      if (existing) {
        // Update quantity for existing item
        await supabase
          .from('cart_items')
          .update({ quantity: existing.quantity + item.quantity })
          .eq('id', existing.id);
      } else {
        // Insert new item
        await supabase
          .from('cart_items')
          .insert({
            cart_id: targetCartId,
            product_id: item.product_id,
            quantity: item.quantity
          });
      }
    }
  };

  const loadCartItems = async (cId: string) => {
    try {
      // Fetch cart items from local DB (no products join)
      const { data: cartData, error } = await supabase
        .from('cart_items')
        .select('id, product_id, quantity')
        .eq('cart_id', cId);

      if (error) throw error;

      if (!cartData || cartData.length === 0) {
        setItems([]);
        return;
      }

      // Fetch product details from CMS API
      const productIds = cartData.map(item => item.product_id);
      
      try {
        // Batch fetch products by IDs from CMS
        const { products } = await cmsApi.getProductsByIds(productIds);

        // Merge cart items with product details
        const formattedItems = cartData.map(item => {
          const product = products.find(p => p.id === item.product_id);
          return {
            id: item.id,
            product_id: item.product_id,
            quantity: item.quantity,
            product: product ? {
              title: product.title,
              price: product.price || 0,
              image_url: product.image_url || '',
              slug: product.slug,
              currency: product.currency || 'USD',
              brand_id: product.specifications?.brand_id || null
            } : undefined
          };
        });

        // Separate valid items from orphaned items (products no longer in CMS)
        const validItems = formattedItems.filter(item => item.product !== undefined);
        const orphanedItems = formattedItems.filter(item => item.product === undefined);

        // Auto-delete orphaned cart items from database
        if (orphanedItems.length > 0) {
          console.warn(`Removing ${orphanedItems.length} orphaned cart items (products no longer in CMS)`);
          const orphanedIds = orphanedItems.map(item => item.id);
          
          await supabase
            .from('cart_items')
            .delete()
            .in('id', orphanedIds);

          toast({
            title: "Cart updated",
            description: `${orphanedItems.length} unavailable item(s) removed`,
          });
        }

        setItems(validItems);
      } catch (cmsError) {
        console.error('Error fetching product details from CMS:', cmsError);
        // Still show cart items even if product details fail
        setItems(cartData.map(item => ({
          id: item.id,
          product_id: item.product_id,
          quantity: item.quantity,
          product: undefined
        })));
      }
    } catch (error) {
      console.error('Error loading cart items:', error);
    }
  };

  const addToCart = useCallback(async (productId: string, showToast: boolean = false, source: string = 'unknown') => {
    if (!cartId) {
      toast({
        title: "Error",
        description: "Cart not initialized",
        variant: "destructive"
      });
      return;
    }

    // Prevent double-clicks
    if (isAdding) return;
    setIsAdding(true);

    try {
      // Optimistic update - check local state first
      const existingItem = items.find(item => item.product_id === productId);

      if (existingItem) {
        // Optimistically update quantity locally
        setItems(prev => prev.map(item => 
          item.product_id === productId 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ));

        // Update in database
        const { error } = await supabase
          .from('cart_items')
          .update({ quantity: existingItem.quantity + 1 })
          .eq('id', existingItem.id);

        if (error) {
          // Rollback optimistic update on error
          setItems(prev => prev.map(item => 
            item.product_id === productId 
              ? { ...item, quantity: item.quantity - 1 }
              : item
          ));
          throw error;
        }
      } else {
        // Use upsert for new items to handle race conditions
        const { data, error } = await supabase
          .from('cart_items')
          .upsert({
            cart_id: cartId,
            product_id: productId,
            quantity: 1
          }, {
            onConflict: 'cart_id,product_id'
          })
          .select('id, product_id, quantity')
          .single();

        if (error) throw error;
        
        // Add to local state WITH product details from CMS
        if (data) {
          // Fetch product details from CMS for the new item
          let productDetails: CartItem['product'] = undefined;
          try {
            const { products } = await cmsApi.getProductsByIds([productId]);
            const product = products.find(p => p.id === productId);
            if (product) {
              productDetails = {
                title: product.title,
                price: product.price || 0,
                image_url: product.image_url || '',
                slug: product.slug,
                currency: product.currency || 'USD',
                brand_id: product.specifications?.brand_id || null
              };
            }
          } catch (cmsError) {
            console.error('Error fetching product details:', cmsError);
          }

          setItems(prev => {
            // Check if already exists (race condition protection)
            const exists = prev.some(item => item.product_id === productId);
            if (exists) {
              return prev.map(item => 
                item.product_id === productId 
                  ? { ...item, id: data.id, quantity: data.quantity, product: productDetails || item.product }
                  : item
              );
            }
            return [...prev, { 
              id: data.id, 
              product_id: data.product_id, 
              quantity: data.quantity,
              product: productDetails
            }];
          });
        }
      }

      // Track add to cart event
      trackEvent({
        type: 'add_to_cart',
        productId,
        category: 'conversion',
        metadata: { quantity: 1, source },
        sessionId: getSessionId(),
        userId: userIdRef.current,
      });

      if (showToast) {
        toast({
          title: "Added to cart",
          description: "Product added successfully"
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add to cart",
        variant: "destructive"
      });
      // Refresh cart to sync state
      await loadCartItems(cartId);
    } finally {
      setIsAdding(false);
    }
  }, [cartId, items, isAdding, toast]);

  const removeFromCart = useCallback(async (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    
    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      
      setItems(prev => prev.filter(item => item.id !== itemId));

      // Track remove from cart event
      if (item) {
        trackEvent({
          type: 'remove_from_cart',
          productId: item.product_id,
          category: 'conversion',
          metadata: { quantity: item.quantity },
          sessionId: getSessionId(),
          userId: userIdRef.current,
        });
      }
      
      toast({
        title: "Removed from cart",
        description: "Item removed successfully"
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast({
        title: "Error",
        description: "Failed to remove item",
        variant: "destructive"
      });
    }
  }, [items, toast]);

  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (quantity < 1) {
      await removeFromCart(itemId);
      return;
    }

    const item = items.find(i => i.id === itemId);
    const oldQuantity = item?.quantity || 0;

    try {
      const { error } = await supabase
        .from('cart_items')
        .update({ quantity })
        .eq('id', itemId);

      if (error) throw error;
      
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      ));

      // Track quantity update event
      if (item) {
        trackEvent({
          type: 'update_cart_quantity',
          productId: item.product_id,
          category: 'conversion',
          metadata: { old_quantity: oldQuantity, new_quantity: quantity },
          sessionId: getSessionId(),
          userId: userIdRef.current,
        });
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast({
        title: "Error",
        description: "Failed to update quantity",
        variant: "destructive"
      });
    }
  }, [items, removeFromCart, toast]);

  const clearCart = useCallback(async () => {
    if (!cartId) return;

    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('cart_id', cartId);

      if (error) throw error;
      setItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  }, [cartId]);

  const refreshCart = useCallback(async () => {
    if (cartId) {
      await loadCartItems(cartId);
    }
  }, [cartId]);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartId,
        items,
        itemCount,
        isLoading,
        isAdding,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        refreshCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
